import type { CSSProperties } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { useEffect, useRef, useState } from "react";
import { SidebarNav } from "./SidebarNav";
import { TopBar } from "./TopBar";
import { MobileNavDrawer } from "./MobileNavDrawer";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { getCookie, setCookie } from "../../lib/cookies";
import { Toaster } from "../ui/Toaster";
import { useMe } from "../../hooks/useMe";
import { useIsDesktop } from "../../hooks/useMediaQuery";
import { isAdminUser } from "../../lib/permissions";
import { usePageTitle } from "../../hooks/usePageTitle";
import { useHashScroll } from "../../hooks/useHashScroll";
import { LayoutProvider, useLayoutContext } from "../../contexts/LayoutContext";
import { cn } from "../../utils/cn";
import { initAuditCollector, stopAuditCollector, trackEvent } from "../../lib/auditCollector";
import { useSystemMeta } from "../../hooks/useSystemMeta";
import { useAutoAdvertiseDashboardUrl } from "../../hooks/useAutoAdvertiseDashboardUrl";
import { DemoBanner } from "./DemoBanner";

/** Inner shell that responds to layout context (bleed strips main padding) */
const MainShell = ({ children }: { children: React.ReactNode }) => {
  const layout = useLayoutContext();
  const bleed = layout?.bleed ?? false;

  return (
    <main
      id="main-content"
      className={cn(
        "flex-1 min-h-0 flex flex-col",
        bleed ? "" : "px-4 py-4 sm:px-6 sm:py-6 lg:px-8",
      )}
      tabIndex={-1}
    >
      {children}
    </main>
  );
};

/** Inner content wrapper that responds to layout context */
const MainContent = () => {
  const layout = useLayoutContext();
  const fullWidth = layout?.fullWidth ?? false;

  return (
    <div
      className={cn(
        "mx-auto w-full",
        fullWidth ? "max-w-none flex-1 min-h-0 min-w-0 flex flex-col" : "max-w-5xl space-y-6"
      )}
    >
      <Outlet />
    </div>
  );
};

export const AppShell = () => {
  const [collapsed, setCollapsed] = useState(() => getCookie("qb_sidebar") === "collapsed");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const { user, appConfig } = useMe();
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  usePageTitle();
  // Cross-page hash-scroll: deep-links (command palette, sidebar) that navigate
  // from another page scroll to their `#section` target once the route mounts.
  useHashScroll();
  // Zero-config transfer discovery: on first admin load, advertise this box's own
  // origin so same-owner peers can deep-link to it with no manual setup. Admin +
  // feature-gated, fire-and-forget, once per page load.
  useAutoAdvertiseDashboardUrl();
  const systemMeta = useSystemMeta({ refreshMs: 300_000 });
  const isDemoMode = systemMeta.data?.isDemoMode === true;
  const maintenanceEnabled = appConfig?.maintenance?.enabled ?? false;
  const maintenanceMessage = appConfig?.maintenance?.message ?? null;
  const showMaintenanceBanner = maintenanceEnabled && isAdminUser(user);

  const handleToggle = () => {
    if (isDesktop) {
      setCollapsed((prev) => !prev);
    } else {
      setMobileNavOpen((prev) => !prev);
    }
  };

  // Close mobile nav when viewport crosses to desktop
  useEffect(() => {
    if (isDesktop) setMobileNavOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    setCookie("qb_sidebar", collapsed ? "collapsed" : "expanded", 365);
  }, [collapsed]);

  // Start audit collector for admin users
  useEffect(() => {
    if (isAdminUser(user)) {
      void initAuditCollector();
      return () => stopAuditCollector();
    }
  }, [user]);

  // Track route navigations
  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;
    trackEvent({ category: "ui", action: "route.navigate", details: { path: location.pathname } });
  }, [location.pathname]);

  return (
    <LayoutProvider>
      <div
        className="min-h-screen bg-background"
        style={{ "--topbar-height": "72px" } as CSSProperties}
      >
        {isDemoMode && <DemoBanner />}
        {/* Skip link for keyboard navigation */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <div className="flex">
          <SidebarNav collapsed={collapsed} />
          <div className="flex min-h-screen flex-1 flex-col min-w-0">
            <TopBar collapsed={collapsed} onToggle={handleToggle} mobileNavOpen={mobileNavOpen} isDesktop={isDesktop} />
            <MainShell>
              <ImpersonationBanner />
              {showMaintenanceBanner && (
                <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <strong className="font-semibold">Maintenance mode enabled.</strong>{" "}
                      {maintenanceMessage ? maintenanceMessage : "Non-admin users are currently blocked."}
                    </div>
                    <Link
                      to="/settings/general"
                      className="text-xs font-semibold uppercase tracking-wide text-amber-900 underline-offset-2 hover:underline dark:text-amber-100"
                    >
                      Manage settings
                    </Link>
                  </div>
                </div>
              )}
              <MainContent />
            </MainShell>
          </div>
        </div>
        <MobileNavDrawer open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
        <Toaster />
      </div>
    </LayoutProvider>
  );
};
